/**
 * FormPreviewPane — embeddable, no-persistence form renderer used inside the
 * form builder. Thin chrome around the shared `<FormRenderer mode="preview">`:
 * the renderer owns conditional logic, calculated fields, validation, soft
 * warnings, and inline justifications. This component only manages the
 * preview-only state (in-memory values + reset + faux submit notice).
 */
import { useMemo, useState } from 'react'
import { AlertCircle, AlertTriangle, Send, RotateCcw, MessageSquareWarning, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import type { FormDefinition } from '../api/form-builder-api'
import { FormRenderer, runFormEngine } from '@/lib/form-engine'

export interface FormPreviewPaneProps {
  definition: FormDefinition
  title?: string
  banner?: string
  className?: string
}

export function FormPreviewPane({ definition, title, banner, className }: FormPreviewPaneProps) {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [justifications, setJustifications] = useState<Record<string, string>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [submitOk, setSubmitOk] = useState(false)
  const [submitNotice, setSubmitNotice] = useState<string | null>(null)

  const snapshot = useMemo(
    () => runFormEngine(definition.fields, definition.conditional_rules ?? [], values),
    [definition.fields, definition.conditional_rules, values],
  )

  const errorCount = Object.keys(snapshot.blockingErrors).length
  const warningCount = snapshot.softWarnings.length
  const missingJustifications = snapshot.softWarnings.filter(
    (w) => !(justifications[`${w.fieldKey}_${w.ruleId}`] ?? '').trim(),
  )
  const visibleCount = definition.fields.filter(
    (f) => snapshot.renderStates[f.field_key]?.visible !== false,
  ).length
  const hiddenCount = definition.fields.length - visibleCount

  const setField = (key: string, v: unknown) => {
    setValues((prev) => ({ ...prev, [key]: v }))
    setSubmitOk(false)
    setSubmitNotice(null)
  }

  const handleSubmit = () => {
    setSubmitAttempted(true)
    if (errorCount > 0) {
      setSubmitNotice('لطفاً ابتدا خطاهای بلوک‌کننده را رفع کنید')
      setSubmitOk(false)
      return
    }
    if (missingJustifications.length > 0) {
      setSubmitNotice(
        `لطفاً برای ${missingJustifications.length.toLocaleString('fa-IR')} هشدار، توجیه بالینی وارد کنید`,
      )
      setSubmitOk(false)
      return
    }
    setSubmitNotice(null)
    setSubmitOk(true)
  }

  const reset = () => {
    setValues({})
    setJustifications({})
    setSubmitOk(false)
    setSubmitNotice(null)
    setSubmitAttempted(false)
  }

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {banner && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/25 px-4 py-1.5 text-xs text-yellow-400">
          {banner}
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 border-b border-border shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {title && <h2 className="text-sm font-semibold text-foreground truncate">{title}</h2>}
          <span className="text-[10px] text-muted-foreground">
            {visibleCount.toLocaleString('fa-IR')} فعال
            {hiddenCount > 0 && ` / ${hiddenCount.toLocaleString('fa-IR')} پنهان`}
          </span>
          {errorCount > 0 && (
            <span className="text-[10px] text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errorCount.toLocaleString('fa-IR')}
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-[10px] text-yellow-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {warningCount.toLocaleString('fa-IR')}
            </span>
          )}
        </div>
        <Button size="sm" variant="ghost" className="h-6 text-xs gap-1" onClick={reset}>
          <RotateCcw className="w-3 h-3" />
          ریست
        </Button>
      </div>

      {submitOk && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-xs text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          فرم اعتبارسنجی شد — آماده ارسال
        </div>
      )}

      {submitNotice && !submitOk && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2 text-xs text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {submitNotice}
        </div>
      )}

      {warningCount > 0 && missingJustifications.length > 0 && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2 text-xs text-yellow-400">
          <MessageSquareWarning className="w-3.5 h-3.5 shrink-0" />
          {missingJustifications.length.toLocaleString('fa-IR')} هشدار نیاز به توجیه بالینی دارد (زیر هر فیلد)
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-xl mx-auto">
          <FormRenderer
            definition={definition}
            values={values}
            onChange={setField}
            mode="preview"
            submitAttempted={submitAttempted}
            justifications={justifications}
            onJustificationChange={(jKey, val) => {
              setJustifications((prev) => ({ ...prev, [jKey]: val }))
              setSubmitNotice(null)
            }}
          />

          {definition.fields.length > 0 && (
            <div className="pt-3 flex justify-end">
              <Button size="sm" className="gap-1.5" onClick={handleSubmit} disabled={errorCount > 0}>
                <Send className="w-3.5 h-3.5" />
                ارسال (پیش‌نمایش)
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
